import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchWrapper } from "../_helpers";

// Slice Config
const name = "inventoryHistory";
const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();

const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder) => createExtraReducers(builder),
});

// Exports
export const inventoryHistoryActions = { ...slice.actions, ...extraActions };
export const inventoryHistoryReducer = slice.reducer;

// Initial State
function createInitialState() {
  return {
    inventoryHistoryList: [],
    stockQuantity: [],
    error: null,
    inventoryHistoryResponse: [],
    transactionProductReport: [],
    productExpirationList: [],
    bulkInventoryResponse: [],
    transactionReportList: [],
    merchandiseReceptionProductList: [],
    merchandiseReceptionForDropdown: [],
    IsLocked: false,
    isMRUpdate: false,
    bulkInsertInventoryForMRResponse: [],
    isDeleteMRUpdate: false,
    IsUnLocked: false,
    MRHistoryListReport: [],
    MRDiscrepancyListReport: [],
    MRPurchaseOrderListReport: [],
  };
}

// Reducers
function createReducers() {
  return {
    clearInventoryHistoryResponse: (state) => {
      state.inventoryHistoryResponse = [];
    },
    clearProductInventoryReport: (state) => {
      state.transactionProductReport = [];
    },
    clearMRPurchaseOrderReport: (state) => {
      state.MRPurchaseOrderListReport = [];
    },
  };
}

// Extra Actions
function createExtraActions() {
  const baseUrl = `${process.env.REACT_APP_API_URL}/Inventory`;

  return {
    getInventoryHistoryListByID: getInventoryHistoryListByID(),
    getStockQuantity: getStockQuantity(),
    insertTransactionHistory: insertTransactionHistory(),
    getTransactionProductReport: getTransactionProductReport(),
    getProductExpirationReport: getProductExpirationReport(),
    bulkInsertInventory: bulkInsertInventory(),
    getTransactionReportByTypeID: getTransactionReportByTypeID(),
    addMerchandiseReception: addMerchandiseReception(),
    GetMerchandiseReceptionForDropdown: GetMerchandiseReceptionForDropdown(),
    GetMerchandiseReceptionProductByMRID:
      GetMerchandiseReceptionProductByMRID(),
    TryLockMerchandiseReceptionProduct: TryLockMerchandiseReceptionProduct(),
    UpdateMerchandiseReceptionProduct: UpdateMerchandiseReceptionProduct(),
    bulkInsertInventoryForMR: bulkInsertInventoryForMR(),
    DeleteMerchandiseReceptionProduct: DeleteMerchandiseReceptionProduct(),
    ReleaseLockMerchandiseReceptionProduct:
      ReleaseLockMerchandiseReceptionProduct(),
    getMRHistoryReport: getMRHistoryReport(),
    getMRDiscrepancyReport: getMRDiscrepancyReport(),
    getMRPurchaseOrderReport: getMRPurchaseOrderReport(),
  };

  function getInventoryHistoryListByID() {
    return createAsyncThunk(
      `${name}/GetInventoryHistoryDetails`,
      async (InventoryHistoryDetail) =>
        await fetchWrapper.post(
          `${baseUrl}/GetInventoryHistoryDetailsByID?ProductID=${InventoryHistoryDetail.ProductID}`
        )
    );
  }

  function getStockQuantity() {
    return createAsyncThunk(
      `${name}/GetProductQuantity`,
      async (ProductDetail) =>
        await fetchWrapper.get(
          `${baseUrl}/GetProductQuantity?ProductID=${ProductDetail.ProductID}`
        )
    );
  }

  function insertTransactionHistory() {
    return createAsyncThunk(
      `${name}/inventoryHistorySave`,
      async (addFormData) =>
        await fetchWrapper.post(
          `${baseUrl}/InsertTransactionHistory`,
          addFormData
        )
    );
  }

  function bulkInsertInventory() {
    return createAsyncThunk(
      `${name}/bulkInsertInventory`,
      async (BulkInventory) =>
        await fetchWrapper.post(`${baseUrl}/BulkInsertInventory`, BulkInventory)
    );
  }

  function addMerchandiseReception() {
    return createAsyncThunk(
      `${name}/addMerchandiseReception`,
      async (formData) =>
        await fetchWrapper.filePost(
          `${baseUrl}/AddMerchandiseReception`,
          formData
        )
    );
  }

  function GetMerchandiseReceptionProductByMRID() {
    return createAsyncThunk(
      `${name}/GetMerchandiseReceptionProductByMRID`,
      async ({ MerchandiseReceptionID, Status, CurrentUserID }) =>
        await fetchWrapper.get(
          `${baseUrl}/GetMerchandiseReceptionProductByMRID?MerchandiseReceptionID=${MerchandiseReceptionID}&Status=${Status}&CurrentUserID=${CurrentUserID}`
        )
    );
  }

  function TryLockMerchandiseReceptionProduct() {
    return createAsyncThunk(
      `${name}/TryLockMerchandiseReceptionProduct`,
      async ({ merchandiseReceptionProductID, currentUserID }) =>
        await fetchWrapper.get(
          `${baseUrl}/TryLockMerchandiseReceptionProduct?merchandiseReceptionProductID=${merchandiseReceptionProductID}&userId=${currentUserID}`
        )
    );
  }
  function ReleaseLockMerchandiseReceptionProduct() {
    return createAsyncThunk(
      `${name}/ReleaseLockMerchandiseReceptionProduct`,
      async ({ merchandiseReceptionProductID }) =>
        await fetchWrapper.get(
          `${baseUrl}/ReleaseLockMerchandiseReceptionProduct?merchandiseReceptionProductID=${merchandiseReceptionProductID}`
        )
    );
  }

  function getMRHistoryReport() {
    return createAsyncThunk(
      `${name}/GetMRHistoryReport`,
      async () => await fetchWrapper.get(`${baseUrl}/GetMRHistoryReport`)
    );
  }
  function getMRDiscrepancyReport() {
    return createAsyncThunk(
      `${name}/GetMRDiscrepancyReport`,
      async () => await fetchWrapper.get(`${baseUrl}/GetMRDiscrepancyReport`)
    );
  }
  function getMRPurchaseOrderReport() {
    return createAsyncThunk(
      `${name}/GetMRPurchaseOrderReport`,
      async (MerchandiseReceptionID) =>
        await fetchWrapper.get(
          `${baseUrl}/GetMRPurchaseOrderReport?MerchandiseReceptionID=${MerchandiseReceptionID}`
        )
    );
  }

  function getTransactionProductReport() {
    return createAsyncThunk(
      `${name}/ProductTransactionReport`,
      async (productID) => {
        const res = await fetchWrapper.post(
          `${baseUrl}/ProductTransactionReport?ProductID=${productID}`
        );
        return res;
      }
    );
  }

  function getProductExpirationReport() {
    return createAsyncThunk(`${name}/getProductExpirationReport`, async () => {
      const res = await fetchWrapper.get(
        `${baseUrl}/GetProductExpirationReport`
      );
      return res;
    });
  }

  function getTransactionReportByTypeID() {
    return createAsyncThunk(
      `${name}/getTransactionReportByTypeID`,
      async (data) =>
        await fetchWrapper.post(`${baseUrl}/GetTransactionReportByTypeID`, data)
    );
  }

  function GetMerchandiseReceptionForDropdown() {
    return createAsyncThunk(
      `${name}/GetMerchandiseReceptionForDropdown`,
      async ({ MerchandiseReceptionID, IsCompleted, Status }) =>
        await fetchWrapper.get(
          `${baseUrl}/GetMerchandiseReceptionForDropdown?MerchandiseReceptionID=${MerchandiseReceptionID}&IsCompleted=${IsCompleted}&Status=${Status}`
        )
    );
  }

  function UpdateMerchandiseReceptionProduct() {
    return createAsyncThunk(
      `${name}/UpdateMerchandiseReceptionProduct`,
      async (updatedItem) =>
        await fetchWrapper.post(
          `${baseUrl}/UpdateMerchandiseReceptionProduct`,
          updatedItem
        )
    );
  }

  function bulkInsertInventoryForMR() {
    return createAsyncThunk(
      `${name}/bulkInsertInventoryForMR`,
      async (MerchandiseReception) =>
        await fetchWrapper.post(
          `${baseUrl}/InsertInventoryForMR`,
          MerchandiseReception
        )
    );
  }

  function DeleteMerchandiseReceptionProduct() {
    return createAsyncThunk(
      `${name}/DeleteMerchandiseReceptionProduct`,
      async ({ MerchandiseReceptionProductID, CurrentUserID }) =>
        await fetchWrapper.get(
          `${baseUrl}/DeleteMerchandiseReceptionProduct?merchandiseReceptionProductID=${MerchandiseReceptionProductID}` +
            `&CurrentUserID=${CurrentUserID}`
        )
    );
  }
}

// Extra Reducers
function createExtraReducers(builder) {
  builder
    .addCase(extraActions.getInventoryHistoryListByID.pending, (state) => {
      state.error = "";
    })
    .addCase(
      extraActions.getInventoryHistoryListByID.fulfilled,
      (state, action) => {
        state.inventoryHistoryList = action.payload || [];
        state.inventoryHistoryResponse = [];
      }
    )
    .addCase(
      extraActions.getInventoryHistoryListByID.rejected,
      (state, action) => {
        state.error = { error: action.error?.message || "Error fetching data" };
      }
    )
    .addCase(extraActions.getStockQuantity.pending, (state) => {
      state.error = "";
    })
    .addCase(extraActions.getStockQuantity.fulfilled, (state, action) => {
      state.stockQuantity = action.payload || 0;
    })
    .addCase(extraActions.getStockQuantity.rejected, (state, action) => {
      state.error = {
        error: action.error?.message || "Error fetching stock quantity",
      };
    })
    .addCase(extraActions.insertTransactionHistory.pending, (state) => {
      state.inventoryHistoryResponse = { loading: true };
    })
    .addCase(
      extraActions.insertTransactionHistory.fulfilled,
      (state, action) => {
        state.inventoryHistoryResponse = action.payload;
      }
    )
    .addCase(
      extraActions.insertTransactionHistory.rejected,
      (state, action) => {
        state.inventoryHistoryResponse = {
          error: action.error?.message || "Transaction failed",
        };
      }
    )
    .addCase(extraActions.getTransactionProductReport.pending, (state) => {
      state.transactionProductReport = { loading: true };
    })
    .addCase(
      extraActions.getTransactionProductReport.fulfilled,
      (state, action) => {
        state.transactionProductReport = action.payload;
      }
    )
    .addCase(
      extraActions.getTransactionProductReport.rejected,
      (state, action) => {
        state.transactionProductReport = {
          error: action.error?.message || "Error fetching report",
        };
      }
    )
    //getProductExpirationReport
    .addCase(extraActions.getProductExpirationReport.pending, (state) => {
      state.productExpirationList = [];
    })
    .addCase(
      extraActions.getProductExpirationReport.fulfilled,
      (state, action) => {
        state.productExpirationList = action.payload;
      }
    )
    .addCase(
      extraActions.getProductExpirationReport.rejected,
      (state, action) => {
        state.productExpirationList = [];
      }
    )
    //bulkInsertInventory
    .addCase(extraActions.bulkInsertInventory.pending, (state) => {
      state.bulkInsertInventoryResponse = [];
    })
    .addCase(extraActions.bulkInsertInventory.fulfilled, (state, action) => {
      state.bulkInsertInventoryResponse = action.payload;
    })
    .addCase(extraActions.bulkInsertInventory.rejected, (state, action) => {
      state.bulkInsertInventoryResponse = {
        error: action.error?.message || "Bulk insert failed",
      };
    })
    //getTransactionReportByTypeID
    .addCase(extraActions.getTransactionReportByTypeID.pending, (state) => {
      state.transactionReportList = [];
    })
    .addCase(
      extraActions.getTransactionReportByTypeID.fulfilled,
      (state, action) => {
        state.transactionReportList = action.payload;
      }
    )
    .addCase(
      extraActions.getTransactionReportByTypeID.rejected,
      (state, action) => {
        state.transactionReportList = [];
      }
    ) //addMerchandiseReception
    .addCase(extraActions.addMerchandiseReception.pending, (state) => {
      state.merchandiseReceptionProductList = [];
    })
    .addCase(
      extraActions.addMerchandiseReception.fulfilled,
      (state, action) => {
        state.merchandiseReceptionProductList = action.payload;
      }
    )
    .addCase(extraActions.addMerchandiseReception.rejected, (state, action) => {
      state.merchandiseReceptionProductList = [];
    })
    // GetMerchandiseReceptionForDropdown
    .addCase(
      extraActions.GetMerchandiseReceptionForDropdown.pending,
      (state) => {
        state.merchandiseReceptionForDropdown = [];
      }
    )
    .addCase(
      extraActions.GetMerchandiseReceptionForDropdown.fulfilled,
      (state, action) => {
        state.merchandiseReceptionForDropdown = action.payload;
      }
    )
    .addCase(
      extraActions.GetMerchandiseReceptionForDropdown.rejected,
      (state, action) => {
        state.merchandiseReceptionForDropdown = [];
      }
    )

    //GetMerchandiseReceptionProductByMRID
    .addCase(
      extraActions.GetMerchandiseReceptionProductByMRID.pending,
      (state) => {
        state.merchandiseReceptionProductList = [];
      }
    )
    .addCase(
      extraActions.GetMerchandiseReceptionProductByMRID.fulfilled,
      (state, action) => {
        state.merchandiseReceptionProductList = action.payload;
      }
    )
    .addCase(
      extraActions.GetMerchandiseReceptionProductByMRID.rejected,
      (state, action) => {
        state.merchandiseReceptionProductList = [];
      }
    )
    // TryLockMerchandiseReceptionProduct
    .addCase(
      extraActions.TryLockMerchandiseReceptionProduct.pending,
      (state) => {
        state.IsLocked = false;
      }
    )
    .addCase(
      extraActions.TryLockMerchandiseReceptionProduct.fulfilled,
      (state, action) => {
        state.IsLocked = action.payload;
      }
    )
    .addCase(
      extraActions.TryLockMerchandiseReceptionProduct.rejected,
      (state, action) => {
        state.IsLocked = false;
      }
    )
    //UpdateMerchandiseReceptionProduct
    .addCase(
      extraActions.UpdateMerchandiseReceptionProduct.pending,
      (state) => {
        state.isMRUpdate = false;
      }
    )
    .addCase(
      extraActions.UpdateMerchandiseReceptionProduct.fulfilled,
      (state, action) => {
        state.isMRUpdate = action.payload;
      }
    )
    .addCase(
      extraActions.UpdateMerchandiseReceptionProduct.rejected,
      (state, action) => {
        state.isMRUpdate = false;
      }
    )
    //bulkInsertInventoryForMRResponse
    .addCase(extraActions.bulkInsertInventoryForMR.pending, (state) => {
      state.bulkInsertInventoryForMRResponse = [];
    })
    .addCase(
      extraActions.bulkInsertInventoryForMR.fulfilled,
      (state, action) => {
        state.bulkInsertInventoryForMRResponse = action.payload;
      }
    )
    .addCase(
      extraActions.bulkInsertInventoryForMR.rejected,
      (state, action) => {
        state.bulkInsertInventoryForMRResponse = {
          error: action.error?.message || "Bulk insert failed",
        };
      }
    )
    //DeleteMerchandiseReceptionProduct
    .addCase(
      extraActions.DeleteMerchandiseReceptionProduct.pending,
      (state) => {
        state.isDeleteMRUpdate = false;
      }
    )
    .addCase(
      extraActions.DeleteMerchandiseReceptionProduct.fulfilled,
      (state, action) => {
        state.isDeleteMRUpdate = action.payload;
      }
    )
    .addCase(
      extraActions.DeleteMerchandiseReceptionProduct.rejected,
      (state, action) => {
        state.isDeleteMRUpdate = false;
      }
    )
    //ReleaseLockMerchandiseReceptionProduct
    .addCase(
      extraActions.ReleaseLockMerchandiseReceptionProduct.pending,
      (state) => {
        state.IsUnLocked = false;
      }
    )
    .addCase(
      extraActions.ReleaseLockMerchandiseReceptionProduct.fulfilled,
      (state, action) => {
        state.IsUnLocked = action.payload;
      }
    )
    .addCase(
      extraActions.ReleaseLockMerchandiseReceptionProduct.rejected,
      (state, action) => {
        state.IsUnLocked = false;
      }
    )
    //getMRHistoryReport
    .addCase(extraActions.getMRHistoryReport.pending, (state) => {
      state.MRHistoryListReport = [];
    })
    .addCase(extraActions.getMRHistoryReport.fulfilled, (state, action) => {
      state.MRHistoryListReport = action.payload || [];
    })
    .addCase(extraActions.getMRHistoryReport.rejected, (state, action) => {
      state.MRHistoryListReport = [];
    })
    //getMRDiscrepancyReport
    .addCase(extraActions.getMRDiscrepancyReport.pending, (state) => {
      state.MRDiscrepancyListReport = [];
    })
    .addCase(extraActions.getMRDiscrepancyReport.fulfilled, (state, action) => {
      state.MRDiscrepancyListReport = action.payload || [];
    })
    .addCase(extraActions.getMRDiscrepancyReport.rejected, (state, action) => {
      state.MRDiscrepancyListReport = [];
    })
    //getMRPurchaseOrderReport
    .addCase(extraActions.getMRPurchaseOrderReport.pending, (state) => {
      state.MRPurchaseOrderListReport = [];
    })
    .addCase(
      extraActions.getMRPurchaseOrderReport.fulfilled,
      (state, action) => {
        state.MRPurchaseOrderListReport = action.payload || [];
      }
    )
    .addCase(
      extraActions.getMRPurchaseOrderReport.rejected,
      (state, action) => {
        state.MRPurchaseOrderListReport = [];
      }
    );
}
